import { useEffect, useMemo, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { formatMoney } from "@/lib/money";
import {
  Users,
  ArrowLeftRight,
  Gift,
  ShieldAlert,
  Sparkles,
  Wallet,
  Store,
  TrendingUp,
  RefreshCw,
  Loader2,
} from "lucide-react";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
} from "recharts";

interface Metrics {
  total_users: number;
  new_users_24h: number;
  active_users_24h: number;
  tx_count_24h: number;
  tx_volume_cents_24h: number;
  tx_volume_cents_all: number;
  rewards_cents_24h: number;
  spin_cash_24h: number;
  spin_count_24h: number;
  pending_referrals: number;
  qualified_referrals: number;
  flagged_events_24h: number;
  wallet_float_cents: number;
  merchants: number;
  admins: number;
}

interface SeriesRow {
  day: string;
  tx_count: number;
  volume_cents: number;
}

const KpiCard = ({
  label,
  value,
  hint,
  icon: Icon,
  tone = "default",
}: {
  label: string;
  value: string;
  hint?: string;
  icon: React.ComponentType<{ className?: string }>;
  tone?: "default" | "warn" | "good";
}) => (
  <Card>
    <CardContent className="p-4">
      <div className="flex items-start justify-between gap-2">
        <div className="space-y-1 min-w-0">
          <div className="text-xs uppercase tracking-wider text-muted-foreground">{label}</div>
          <div className="text-2xl font-bold tabular-nums truncate">{value}</div>
          {hint && <div className="text-xs text-muted-foreground">{hint}</div>}
        </div>
        <div
          className={
            tone === "warn"
              ? "h-9 w-9 rounded-md grid place-items-center bg-destructive/10 text-destructive"
              : tone === "good"
                ? "h-9 w-9 rounded-md grid place-items-center bg-primary/10 text-primary"
                : "h-9 w-9 rounded-md grid place-items-center bg-muted text-muted-foreground"
          }
        >
          <Icon className="h-4 w-4" />
        </div>
      </div>
    </CardContent>
  </Card>
);

const AdminDashboard = () => {
  const [metrics, setMetrics] = useState<Metrics | null>(null);
  const [series, setSeries] = useState<SeriesRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const load = async () => {
    setRefreshing(true);
    const [m, s] = await Promise.all([
      supabase.rpc("admin_dashboard_metrics"),
      supabase.rpc("admin_tx_series", { _days: 14 }),
    ]);
    if (m.error) toast.error(m.error.message);
    else setMetrics(m.data as unknown as Metrics);
    if (s.error) toast.error(s.error.message);
    else setSeries((s.data ?? []) as SeriesRow[]);
    setLoading(false);
    setRefreshing(false);
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, []);

  const chartData = useMemo(
    () =>
      series.map((r) => ({
        day: new Date(r.day).toLocaleDateString(undefined, { month: "short", day: "numeric" }),
        Transactions: r.tx_count,
        "Volume ($)": Number(r.volume_cents) / 100,
      })),
    [series],
  );

  if (loading) {
    return (
      <div className="p-6 grid place-items-center min-h-[60vh] text-muted-foreground">
        <Loader2 className="h-5 w-5 animate-spin" />
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6 space-y-4">
      <div className="flex items-center justify-between gap-2">
        <div>
          <h1 className="text-xl md:text-2xl font-bold">Dashboard</h1>
          <p className="text-sm text-muted-foreground">Live metrics across the platform.</p>
        </div>
        <Button variant="outline" size="sm" onClick={load} disabled={refreshing}>
          <RefreshCw className={refreshing ? "h-4 w-4 animate-spin" : "h-4 w-4"} />
          Refresh
        </Button>
      </div>

      {metrics && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <KpiCard
              label="Total users"
              value={metrics.total_users.toLocaleString()}
              hint={`+${metrics.new_users_24h} in 24h`}
              icon={Users}
            />
            <KpiCard
              label="Active 24h"
              value={metrics.active_users_24h.toLocaleString()}
              hint="Distinct senders"
              icon={TrendingUp}
              tone="good"
            />
            <KpiCard
              label="Transactions 24h"
              value={metrics.tx_count_24h.toLocaleString()}
              hint={formatMoney(metrics.tx_volume_cents_24h) + " volume"}
              icon={ArrowLeftRight}
            />
            <KpiCard
              label="Wallet float"
              value={formatMoney(metrics.wallet_float_cents)}
              hint="Sum of all balances"
              icon={Wallet}
            />
            <KpiCard
              label="Rewards 24h"
              value={formatMoney(metrics.rewards_cents_24h + metrics.spin_cash_24h)}
              hint={`${metrics.spin_count_24h} spins`}
              icon={Gift}
            />
            <KpiCard
              label="Pending referrals"
              value={metrics.pending_referrals.toLocaleString()}
              hint={`${metrics.qualified_referrals} qualified`}
              icon={Sparkles}
              tone={metrics.pending_referrals > 0 ? "warn" : "default"}
            />
            <KpiCard
              label="Flagged events 24h"
              value={metrics.flagged_events_24h.toLocaleString()}
              hint="Security log entries"
              icon={ShieldAlert}
              tone={metrics.flagged_events_24h > 0 ? "warn" : "default"}
            />
            <KpiCard
              label="Merchants"
              value={metrics.merchants.toLocaleString()}
              hint={`${metrics.admins} admins`}
              icon={Store}
            />
          </div>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base">Transactions — last 14 days</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-64 w-full">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={chartData} margin={{ top: 4, right: 8, bottom: 0, left: -16 }}>
                    <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
                    <XAxis dataKey="day" tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 11 }} />
                    <Tooltip
                      contentStyle={{
                        background: "hsl(var(--popover))",
                        border: "1px solid hsl(var(--border))",
                        borderRadius: 8,
                        fontSize: 12,
                      }}
                    />
                    <Line type="monotone" dataKey="Transactions" stroke="hsl(var(--primary))" strokeWidth={2} dot={false} />
                    <Line type="monotone" dataKey="Volume ($)" stroke="hsl(var(--accent-foreground))" strokeWidth={2} dot={false} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base">Operational health</CardTitle>
            </CardHeader>
            <CardContent className="text-sm space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">All-time volume</span>
                <span className="font-semibold tabular-nums">{formatMoney(metrics.tx_volume_cents_all)}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">Spin cash payouts (24h)</span>
                <span className="font-semibold tabular-nums">{formatMoney(metrics.spin_cash_24h)}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">Signup rewards paid (24h)</span>
                <span className="font-semibold tabular-nums">{formatMoney(metrics.rewards_cents_24h)}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">Status</span>
                <Badge variant="secondary">Operational</Badge>
              </div>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
};

export default AdminDashboard;
